// COMISIONES POR ANALISTA — su propia pestaña en Reportes, al lado de
// Comisiones por Póliza (Lissette 2026-10-11).
//
// Lo que se le paga a cada analista NO es la comisión de la póliza: la agente
// pone una cantidad fija por póliza (la base) y niveles según cuántas vendió
// en el mes ("desde 10 pólizas, $35 cada una"). La cuenta está en
// crm-app/comisionDeAnalistas.js; aquí se filtra, se enseña y se editan las
// reglas.
//
// 2026-10-29 (Lissette): *"quiero que cada seat vea solo lo suyo y los agentes
// de todos"*, *"las comisiones y las reglas sean por tipo de póliza"* y *"el
// mismo formato del Reporte de Ventas, pero para comisiones de analista"*.
//   - El analista es el de la póliza (campo Analista = quien la vendió). Un
//     ASIENTO ve solo las filas que son suyas (su nombre, o el usuario al que
//     la agente vinculó ese analista en las reglas). El servidor tampoco le
//     manda las reglas de los compañeros (utils/reglasDeAnalistas.ts).
//   - Las reglas tienen una TABLA GENERAL para todos y, si hace falta, montos
//     propios de cada analista; las dos pueden ir por tipo de póliza.
//   - Filtros como el Reporte de Ventas (fechas de venta, tipo, compañía,
//     analista), tarjetas, resumen por analista, detalle de ventas y exportar.
//
// 2026-10-30 (Lissette): *"quiero que solo muestre las activas"*. Solo cuentan
// las pólizas ACTIVAS (mismo `esActiva` que el Excel de clientes): una
// cancelada, vencida o pendiente ni sale ni suma para los niveles del mes.
//
// Las reglas viven en los ajustes de la agencia (`analystCommissionRules`) y
// solo las cambia la agente o la dueña: el servidor no las acepta por la
// puerta de los asientos.
import { useEffect, useMemo, useState } from 'react';
import { Users, Settings2, Plus, Trash2, ChevronDown, ChevronRight, Filter, X, Calendar, DollarSign, Download, AlertCircle } from 'lucide-react';
import { Modal } from './ui.jsx';
import { toast } from './Toast.jsx';
import { fmt, fmtMoney, formatClientName } from '../utils.js';
import { POLICY_TYPES, getPolicyType } from '../policyTypes.js';
import { getAgencySettings, updateAgencySettings } from '../api.js';
import DateField from './DateField.jsx';
import { exportToExcel, exportToCSV } from './SalesReportView.jsx';
import { esActiva } from '../polizasActivasParaExcel.js';
import { allLeadSources, leadTypeLabel } from '../leadSources.js';
import { GroupedChart } from './teamReportChart.jsx';
import {
  reporteDeAnalistasEntre, normalizarReglas, claveDeAnalista, claveDeTipo, esDelUsuario, GENERAL, mesDe,
  normalizarNombre, plantillaDe,
} from '../comisionDeAnalistas.js';

const dosDigitos = (n) => String(n).padStart(2, '0');
const hoyLocal = () => { const d = new Date(); return `${d.getFullYear()}-${dosDigitos(d.getMonth() + 1)}-${dosDigitos(d.getDate())}`; };
const rangoDeMes = (desplazamiento = 0) => {
  const d = new Date();
  const ini = new Date(d.getFullYear(), d.getMonth() + desplazamiento, 1);
  const fin = new Date(d.getFullYear(), d.getMonth() + desplazamiento + 1, 0);
  const f = (x) => `${x.getFullYear()}-${dosDigitos(x.getMonth() + 1)}-${dosDigitos(x.getDate())}`;
  return { desde: f(ini), hasta: f(fin) };
};
const rangoDelAno = () => { const y = new Date().getFullYear(); return { desde: `${y}-01-01`, hasta: `${y}-12-31` }; };

const nombreDeTipo = (id, es) => {
  const pt = POLICY_TYPES.find((p) => p.id === id);
  if (!pt) return id || (es ? 'Sin tipo' : 'No type');
  return `${pt.icon ? `${pt.icon} ` : ''}${es ? (pt.labelEs || pt.label) : pt.label}`;
};
const agenteDe = (p) => (p.agentLabel || p.agentName || '').trim();
const textoDeOrigen = (pago, es) => {
  if (!pago || !pago.origen) return es ? 'Sin regla' : 'No rule';
  const nivel = pago.nivel ? (es ? ` · desde ${pago.nivel.desde}` : ` · ${pago.nivel.desde}+`) : '';
  const de = {
    'analista-especial': es ? 'Suya, precio especial' : 'Own, special price',
    'analista-tipo': es ? 'Suya, por tipo' : 'Own, by type',
    analista: es ? 'Suya' : 'Own',
    'general-especial': es ? 'General, precio especial' : 'General, special price',
    'general-tipo': es ? 'General, por tipo' : 'General, by type',
    general: es ? 'General' : 'General',
    'plantilla-especial': `${es ? 'Regla' : 'Rule'} «${pago.regla || ''}», ${es ? 'precio especial' : 'special price'}`,
    'plantilla-tipo': `${es ? 'Regla' : 'Rule'} «${pago.regla || ''}», ${es ? 'por tipo' : 'by type'}`,
    plantilla: `${es ? 'Regla' : 'Rule'} «${pago.regla || ''}»`,
    prima: es ? '% de la prima' : '% of premium',
  }[pago.origen];
  const miembros = pago.miembros > 1 ? (es ? ` · × ${pago.miembros} miembros` : ` · × ${pago.miembros} members`) : '';
  const ajustes = pago.ajustes && pago.ajustes.length ? (es ? ` · ${pago.ajustes.length} ajuste(s)` : ` · ${pago.ajustes.length} adjustment(s)`) : '';
  return `${de}${nivel}${miembros}${ajustes}`;
};

const Campo = ({ label, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
    <label style={{ fontSize: '.66rem', color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</label>
    {children}
  </div>
);

const Tarjeta = ({ titulo, valor, pie, color, Icono }) => (
  <div className="dkt-card" style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: 4, borderLeft: `3px solid ${color}` }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span style={{ fontSize: 10, color: 'var(--c-text3,#45688a)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em' }}>{titulo}</span>
      <Icono size={14} style={{ color }} />
    </div>
    <div style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--c-white,#eef6ff)', fontFamily: 'var(--font-display)', lineHeight: 1 }}>{valor}</div>
    <div style={{ fontSize: 11, color: 'var(--c-text3,#45688a)' }}>{pie}</div>
  </div>
);

export function AnalystCommissionsView({ policies = [], clients = [], users = [], currentUser = null, lang, puedeEditar = true, userPrefs = {}, onPrefsChange }) {
  const es = lang === 'es';
  // Un ASIENTO (en la pantalla, role 'assistant') ve solo lo suyo.
  const esAsiento = currentUser?.role === 'assistant';
  const guardados = (userPrefs && userPrefs.analystCommissionFilters) || {};
  const mesDeHoy = rangoDeMes(0);
  const [desde, setDesde] = useState(guardados.desde || mesDeHoy.desde);
  const [hasta, setHasta] = useState(guardados.hasta || mesDeHoy.hasta);
  const [fTipo, setFTipo] = useState(guardados.fTipo || '');
  const [fCompania, setFCompania] = useState(guardados.fCompania || '');
  const [fAnalista, setFAnalista] = useState(esAsiento ? '' : (guardados.fAnalista || ''));
  const [reglas, setReglas] = useState({});
  const [cargado, setCargado] = useState(false);
  const [editando, setEditando] = useState(null); // nombre del analista, GENERAL, o '' para uno nuevo
  const [abierto, setAbierto] = useState(null);
  const apiMode = typeof window !== 'undefined' && !!window.__deskteAuth;

  useEffect(() => {
    if (!onPrefsChange) return;
    onPrefsChange((p) => ({ ...p, analystCommissionFilters: { desde, hasta, fTipo, fCompania, fAnalista } }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desde, hasta, fTipo, fCompania, fAnalista]);

  useEffect(() => {
    if (!apiMode) { setCargado(true); return undefined; }
    let vivo = true;
    getAgencySettings()
      .then((a) => { if (vivo) setReglas(normalizarReglas(a?.analystCommissionRules)); })
      .catch((e) => toast.error((es ? 'No se pudieron leer las reglas: ' : 'Could not load the rules: ') + (e.message || e)))
      .finally(() => { if (vivo) setCargado(true); });
    return () => { vivo = false; };
  }, [apiMode]);

  const yo = useMemo(() => (currentUser ? { id: currentUser.id, name: currentUser.name, displayName: currentUser.displayName } : null), [currentUser]);
  const clientById = useMemo(() => Object.fromEntries((clients || []).map((c) => [c.id, c])), [clients]);
  const nombreDeCliente = (p) => { const c = clientById[p.clientId]; return c ? formatClientName(c) : (p.clientName || '—'); };

  // Solo las pólizas ACTIVAS: las demás ni salen ni cuentan para los niveles.
  const activas = useMemo(() => (policies || []).filter(esActiva), [policies]);

  // Opciones de los filtros: solo lo que existe en las pólizas que esta persona puede ver aquí.
  const deEstaPersona = useMemo(
    () => (esAsiento ? activas.filter((p) => esDelUsuario(claveDeAnalista(p.analystName), yo, reglas)) : activas),
    [activas, esAsiento, yo, reglas],
  );
  const opciones = useMemo(() => {
    const uniq = (arr) => [...new Set(arr.map((s) => String(s || '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    return {
      tipos: [...new Set(deEstaPersona.map((p) => claveDeTipo(p.type)).filter(Boolean))],
      companias: uniq(deEstaPersona.map((p) => p.carrier)),
      analistas: uniq([...activas.map((p) => p.analystName), ...Object.keys(reglas).filter((k) => k !== GENERAL)]),
    };
  }, [deEstaPersona, activas, reglas]);

  const reporte = useMemo(() => reporteDeAnalistasEntre(activas, reglas, {
    desde,
    hasta,
    filtro: (p) => (!fTipo || claveDeTipo(p.type) === fTipo) && (!fCompania || String(p.carrier || '').trim() === fCompania),
    soloAnalistas: esAsiento
      ? (a) => esDelUsuario(a, yo, reglas)
      : (fAnalista ? (a) => a === fAnalista : null),
  }), [activas, reglas, desde, hasta, fTipo, fCompania, fAnalista, esAsiento, yo]);

  // El detalle de ventas: una fila por póliza, la más reciente primero.
  const ventas = useMemo(
    () => reporte.filas.flatMap((f) => f.polizas).sort((a, b) => String(b.saleDate || '').localeCompare(String(a.saleDate || ''))),
    [reporte],
  );
  const sinRegla = reporte.filas.reduce((s, f) => s + f.sinRegla, 0);

  // El gráfico, igual que el del Reporte de Ventas (Lissette 2026-10-30: "lo
  // que no veo son los gráficos"): sobre las MISMAS ventas de la tabla, para
  // que nunca digan cosas distintas.
  const dimensionesDelGrafico = useMemo(() => [
    ...(esAsiento ? [] : [{ key: 'analista', label: es ? 'Analista' : 'Analyst', get: (p) => claveDeAnalista(p.analystName) }]),
    { key: 'tipo', label: es ? 'Tipo de póliza' : 'Policy type', get: (p) => nombreDeTipo(claveDeTipo(p.type), es) },
    { key: 'compania', label: es ? 'Compañía' : 'Carrier', get: (p) => p.carrier },
    { key: 'agente', label: es ? 'Agente' : 'Agent', get: (p) => agenteDe(p) },
    { key: 'mes', label: es ? 'Mes de venta' : 'Sale month', get: (p) => mesDe(p.saleDate), timeline: true },
  ], [es, esAsiento]);
  const medidasDelGrafico = useMemo(() => [
    { key: 'pago', label: es ? 'Comisión del analista $' : 'Analyst commission $', get: (p) => reporte.pagos[p.id]?.monto || 0, unit: 'money' },
  ], [es, reporte]);

  const guardarReglas = async (siguientes) => {
    const limpias = normalizarReglas(siguientes);
    if (!apiMode) { setReglas(limpias); return true; }
    try {
      const guardado = await updateAgencySettings({ analystCommissionRules: limpias });
      setReglas(normalizarReglas(guardado?.analystCommissionRules ?? limpias));
      toast.success(es ? 'Reglas guardadas' : 'Rules saved');
      return true;
    } catch (e) {
      toast.error((es ? 'No se pudieron guardar las reglas: ' : 'Could not save the rules: ') + (e.message || e));
      return false;
    }
  };

  const hayFiltros = fTipo || fCompania || fAnalista || desde !== mesDeHoy.desde || hasta !== mesDeHoy.hasta;
  const limpiar = () => { setFTipo(''); setFCompania(''); setFAnalista(''); setDesde(mesDeHoy.desde); setHasta(mesDeHoy.hasta); };
  const ponerRango = (r) => { setDesde(r.desde); setHasta(r.hasta); };

  const filasParaExportar = () => ventas.map((p) => {
    const pago = reporte.pagos[p.id];
    return {
      [es ? 'Fecha de venta' : 'Sale date']: p.saleDate ? fmt(p.saleDate) : '',
      [es ? 'Cliente' : 'Client']: nombreDeCliente(p),
      [es ? 'Tipo' : 'Type']: nombreDeTipo(claveDeTipo(p.type), es),
      [es ? 'Compañía' : 'Carrier']: p.carrier || '',
      Plan: p.plan || '',
      [es ? 'Analista' : 'Analyst']: claveDeAnalista(p.analystName),
      [es ? 'Agente' : 'Agent']: agenteDe(p),
      [es ? 'Fuente del lead' : 'Lead source']: p.leadType ? leadTypeLabel(p.leadType, es ? 'es' : 'en') : '',
      [es ? 'Miembros pagados' : 'Members paid']: pago ? pago.miembros : 1,
      [es ? 'Regla' : 'Rule']: textoDeOrigen(pago, es),
      [es ? 'Comisión del analista' : 'Analyst commission']: pago ? pago.monto : 0,
    };
  });
  const archivo = `deskte-comisiones-analistas-${hoyLocal()}`;

  const sel = { fontSize: '.82rem', padding: '.45rem .6rem', cursor: 'pointer', minWidth: 0, width: '100%' };
  const th = { padding: '.55rem .75rem', textAlign: 'left', fontSize: '.68rem', textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--c-text3,#45688a)', borderBottom: '1px solid var(--c-border,#1a3550)', whiteSpace: 'nowrap' };
  const td = { padding: '.55rem .75rem', fontSize: '.82rem', color: 'var(--c-text2,#7aa3c0)', borderBottom: '1px solid rgba(26,53,80,.4)', whiteSpace: 'nowrap' };
  const chip = (activo) => ({ fontSize: '.72rem', fontWeight: 700, padding: '.3rem .65rem', borderRadius: 100, cursor: 'pointer', fontFamily: 'inherit',
    border: `1px solid ${activo ? 'var(--c-cyan,#00d4ff)' : 'var(--c-border,#1a3550)'}`, background: activo ? 'rgba(0,212,255,.14)' : 'transparent', color: activo ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text2,#7aa3c0)' });
  const esRango = (r) => desde === r.desde && hasta === r.hasta;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div className="dkt-sec-title" style={{ margin: 0 }}>
            <Users size={16} style={{ marginRight: 6, verticalAlign: -2 }} />
            {esAsiento ? (es ? 'Mis comisiones de analista' : 'My analyst commissions') : (es ? 'Comisiones por Analista' : 'Commissions by Analyst')}
          </div>
          <div className="dkt-sec-sub">
            {esAsiento
              ? (es ? 'Tus ventas activas (las pólizas donde tú eres el analista) y lo que te toca según las reglas de tu agencia. Se llena solo cuando pones tu nombre en Analista y la fecha de venta en la póliza.'
                : 'Your active sales (the policies where you are the analyst) and what you earn under your agency’s rules. It fills in by itself when the policy has your name as Analyst and a sale date.')
              : (es ? 'Lo que se le paga a cada analista por sus ventas activas, con las reglas por tipo de póliza. Se llena solo con el Analista y la fecha de venta de cada póliza.'
                : 'What each analyst earns from their active sales, with the rules by policy type. It fills in by itself from each policy’s Analyst and sale date.')}
          </div>
        </div>
        {puedeEditar && !esAsiento && (
          <button type="button" className="dkt-btn-s" onClick={() => setEditando(GENERAL)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Settings2 size={14} /> {es ? 'Reglas de comisión' : 'Commission rules'}
          </button>
        )}
      </div>

      {/* Filtros, como el Reporte de Ventas */}
      <div className="dkt-card" style={{ padding: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <Filter size={14} style={{ color: 'var(--c-cyan,#00d4ff)' }} />
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--c-white,#eef6ff)', fontFamily: 'var(--font-display)' }}>{es ? 'Filtros' : 'Filters'}</span>
          <button type="button" style={chip(esRango(mesDeHoy))} onClick={() => ponerRango(rangoDeMes(0))}>{es ? 'Este mes' : 'This month'}</button>
          <button type="button" style={chip(esRango(rangoDeMes(-1)))} onClick={() => ponerRango(rangoDeMes(-1))}>{es ? 'Mes pasado' : 'Last month'}</button>
          <button type="button" style={chip(esRango(rangoDelAno()))} onClick={() => ponerRango(rangoDelAno())}>{es ? 'Este año' : 'This year'}</button>
          {hayFiltros && (
            <button type="button" onClick={limpiar} style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4, background: 'transparent', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-text2,#7aa3c0)', fontSize: '.72rem', fontWeight: 600, padding: '.3rem .6rem', borderRadius: 7, cursor: 'pointer' }}>
              <X size={11} /> {es ? 'Limpiar filtros' : 'Clear filters'}
            </button>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: '.7rem' }}>
          <Campo label={es ? 'Venta desde' : 'Sale from'}>
            <DateField style={sel} value={desde} onChange={(e) => setDesde(e.target.value)} lang={es ? 'es' : 'en'} />
          </Campo>
          <Campo label={es ? 'Venta hasta' : 'Sale to'}>
            <DateField style={sel} value={hasta} onChange={(e) => setHasta(e.target.value)} lang={es ? 'es' : 'en'} />
          </Campo>
          <Campo label={es ? 'Tipo de póliza' : 'Policy type'}>
            <select className="dkt-inp" style={sel} value={fTipo} onChange={(e) => setFTipo(e.target.value)}>
              <option value="">{es ? 'Todos' : 'All'}</option>
              {opciones.tipos.map((t) => <option key={t} value={t}>{nombreDeTipo(t, es)}</option>)}
            </select>
          </Campo>
          <Campo label={es ? 'Compañía' : 'Carrier'}>
            <select className="dkt-inp" style={sel} value={fCompania} onChange={(e) => setFCompania(e.target.value)}>
              <option value="">{es ? 'Todas' : 'All'}</option>
              {opciones.companias.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Campo>
          {!esAsiento && (
            <Campo label={es ? 'Analista' : 'Analyst'}>
              <select className="dkt-inp" style={sel} value={fAnalista} onChange={(e) => setFAnalista(e.target.value)}>
                <option value="">{es ? 'Todos' : 'All'}</option>
                {opciones.analistas.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </Campo>
          )}
        </div>
      </div>

      {/* Tarjetas */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: '.75rem' }}>
        <Tarjeta titulo={es ? 'Ventas' : 'Sales'} valor={reporte.ventas} pie={es ? 'pólizas en el filtro' : 'policies in filter'} color="#00d4ff" Icono={Calendar} />
        <Tarjeta titulo={esAsiento ? (es ? 'Te toca' : 'You earn') : (es ? 'A pagar a analistas' : 'To pay analysts')} valor={fmtMoney(reporte.total)} pie={es ? 'suma del filtro actual' : 'sum of current filter'} color="#00ff88" Icono={DollarSign} />
        {!esAsiento && <Tarjeta titulo={es ? 'Analistas' : 'Analysts'} valor={reporte.filas.filter((f) => f.ventas > 0).length} pie={es ? 'con ventas en el filtro' : 'with sales in filter'} color="#a78bfa" Icono={Users} />}
        {sinRegla > 0 && <Tarjeta titulo={es ? 'Sin regla' : 'No rule'} valor={sinRegla} pie={es ? 'ventas que no pagan nada todavía' : 'sales that pay nothing yet'} color="#ffb700" Icono={AlertCircle} />}
      </div>

      <GroupedChart rows={ventas} lang={lang} dimensions={dimensionesDelGrafico} measures={medidasDelGrafico} color="#a78bfa" />

      {esAsiento && cargado && reporte.filas.length === 0 && (
        <div className="dkt-card" style={{ padding: '1rem', fontSize: '.8rem', color: 'var(--c-text2,#7aa3c0)' }}>
          {es
            ? `No hay ventas tuyas en estas fechas. Para que una póliza cuente como tuya, en el campo Analista tiene que decir tu nombre como en Deskte (${currentUser?.name || ''}), y tener fecha de venta. Si lo escriben distinto, pídele a tu agente que te vincule en "Reglas de comisión".`
            : `No sales of yours in these dates. For a policy to count as yours, its Analyst field must have your name as in Deskte (${currentUser?.name || ''}) and a sale date. If it's written differently, ask your agent to link you in "Commission rules".`}
        </div>
      )}

      {/* Resumen por analista */}
      {(!esAsiento || reporte.filas.length > 0) && (
        <div className="dkt-card" style={{ padding: 0, overflowX: 'auto' }}>
          <div style={{ padding: '.8rem 1rem', fontSize: 12, fontWeight: 700, color: 'var(--c-white,#eef6ff)', fontFamily: 'var(--font-display)' }}>
            {esAsiento ? (es ? 'Tu resumen' : 'Your summary') : (es ? 'Resumen por analista' : 'Summary by analyst')}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--c-surface2,#112538)' }}>
                <th style={th}>{es ? 'Analista' : 'Analyst'}</th>
                <th style={th}>{es ? 'Ventas' : 'Sales'}</th>
                <th style={th}>{es ? 'Por tipo de póliza' : 'By policy type'}</th>
                <th style={th}>{es ? 'A pagar' : 'To pay'}</th>
                {puedeEditar && !esAsiento && <th style={th} />}
              </tr>
            </thead>
            <tbody>
              {cargado && reporte.filas.length === 0 && (
                <tr><td colSpan={5} style={{ ...td, textAlign: 'center', padding: '2rem' }}>
                  {es ? 'Nadie vendió en estas fechas (por fecha de venta) y no hay reglas puestas todavía.' : 'Nobody sold in these dates (by sale date) and no rules are set yet.'}
                </td></tr>
              )}
              {reporte.filas.map((f) => (
                <FilaDeAnalista key={f.analista} f={f} es={es} td={td} abierto={abierto === f.analista}
                  onToggle={() => setAbierto(abierto === f.analista ? null : f.analista)}
                  onEditar={puedeEditar && !esAsiento ? () => setEditando(f.analista) : null}
                  pagos={reporte.pagos} nombreDeCliente={nombreDeCliente} />
              ))}
            </tbody>
            {reporte.filas.length > 1 && (
              <tfoot>
                <tr style={{ background: 'var(--c-surface2,#112538)', fontWeight: 800 }}>
                  <td style={{ ...td, color: 'var(--c-white,#eef6ff)' }}>TOTAL</td>
                  <td style={td}>{reporte.ventas}</td>
                  <td style={td} />
                  <td style={{ ...td, color: 'var(--c-green,#00ff88)' }}>{fmtMoney(reporte.total)}</td>
                  {puedeEditar && !esAsiento && <td style={td} />}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {/* Detalle de ventas + exportar */}
      {ventas.length > 0 && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '.75rem', flexWrap: 'wrap', gap: '.5rem' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--c-white,#eef6ff)', fontFamily: 'var(--font-display)' }}>
              {es ? `Ventas (${ventas.length})` : `Sales (${ventas.length})`}
            </span>
            <div style={{ display: 'flex', gap: '.4rem' }}>
              <button type="button" className="dkt-btn-s" onClick={() => exportToExcel(filasParaExportar(), `${archivo}.xlsx`)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Download size={13} /> Excel</button>
              <button type="button" className="dkt-btn-s" onClick={() => exportToCSV(filasParaExportar(), `${archivo}.csv`)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Download size={13} /> CSV</button>
            </div>
          </div>
          <div className="dkt-card" style={{ padding: 0, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--c-surface2,#112538)' }}>
                  <th style={th}>{es ? 'Fecha de venta' : 'Sale date'}</th>
                  <th style={th}>{es ? 'Cliente' : 'Client'}</th>
                  <th style={th}>{es ? 'Tipo' : 'Type'}</th>
                  <th style={th}>{es ? 'Compañía' : 'Carrier'}</th>
                  <th style={th}>Plan</th>
                  {!esAsiento && <th style={th}>{es ? 'Analista' : 'Analyst'}</th>}
                  <th style={th}>{es ? 'Agente' : 'Agent'}</th>
                  <th style={th}>{es ? 'Regla' : 'Rule'}</th>
                  <th style={th}>{es ? 'Comisión del analista' : 'Analyst commission'}</th>
                </tr>
              </thead>
              <tbody>
                {ventas.map((p) => {
                  const pago = reporte.pagos[p.id];
                  return (
                    <tr key={p.id}>
                      <td style={td}>{p.saleDate ? fmt(p.saleDate) : '—'}</td>
                      <td style={{ ...td, color: 'var(--c-white,#eef6ff)' }}>{nombreDeCliente(p)}</td>
                      <td style={td}>{nombreDeTipo(claveDeTipo(p.type), es)}</td>
                      <td style={td}>{p.carrier || '—'}</td>
                      <td style={td}>{p.plan || '—'}</td>
                      {!esAsiento && <td style={td}>{claveDeAnalista(p.analystName)}</td>}
                      <td style={td}>{agenteDe(p) || '—'}</td>
                      <td style={{ ...td, color: pago?.origen ? 'var(--c-text2,#7aa3c0)' : 'var(--c-amber,#ffb700)' }}>{textoDeOrigen(pago, es)}</td>
                      <td style={{ ...td, color: 'var(--c-green,#00ff88)', fontWeight: 700 }}>{fmtMoney(pago ? pago.monto : 0)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: 'var(--c-surface2,#112538)', fontWeight: 800 }}>
                  <td style={{ ...td, color: 'var(--c-white,#eef6ff)' }} colSpan={esAsiento ? 7 : 8}>TOTAL</td>
                  <td style={{ ...td, color: 'var(--c-green,#00ff88)' }}>{fmtMoney(reporte.total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Lo que no entra se dice, no se esconde: una póliza sin analista o sin
          fecha de venta no se le puede pagar a nadie ni en ningún mes. */}
      {!esAsiento && (reporte.sinAnalista > 0 || reporte.sinFecha > 0) && (
        <div style={{ fontSize: '.72rem', color: 'var(--c-amber,#ffb700)' }}>
          {reporte.sinAnalista > 0 && (es ? `${reporte.sinAnalista} póliza(s) vendida(s) en estas fechas no tienen analista. ` : `${reporte.sinAnalista} policy(ies) sold in these dates have no analyst. `)}
          {reporte.sinFecha > 0 && (es ? `${reporte.sinFecha} póliza(s) con analista no tienen fecha de venta y no entran en ninguna fecha.` : `${reporte.sinFecha} policy(ies) with an analyst have no sale date and fall in no date range.`)}
        </div>
      )}
      <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)' }}>
        {es
          ? 'Solo cuentan las pólizas ACTIVAS: una cancelada, vencida o pendiente no sale aquí ni suma para los niveles. Cuentan por su FECHA DE VENTA y para el analista que dice la póliza. Los niveles se cuentan por mes y por tipo de póliza, con todas las ventas activas del mes: un filtro no cambia lo que se paga.'
          : 'Only ACTIVE policies count: a cancelled, expired or pending one does not show here or add to the levels. They count by their SALE DATE and for the analyst on the policy. Levels are counted per month and per policy type, over all the month’s active sales: a filter never changes what is paid.'}
      </div>

      {editando !== null && (
        <EditorDeReglas
          es={es}
          inicial={editando}
          analistas={opciones.analistas}
          companias={opciones.companias}
          usuarios={users}
          reglas={reglas}
          onClose={() => setEditando(null)}
          onGuardar={async (nombre, regla, borrar) => {
            const siguientes = { ...reglas };
            const general = siguientes[GENERAL] || { base: 0, niveles: [] };
            const guardadas = general.plantillas || [];
            if (nombre.startsWith('plantilla:')) {
              // Una regla guardada vive dentro de la tabla general. Cada
              // analista está en UNA: al agregarlo aquí, sale de las demás.
              const id = nombre.slice('plantilla:'.length);
              const aqui = new Set((regla?.analistas || []).map(normalizarNombre));
              let lista = guardadas.map((pl) => (pl.id === id ? null
                : { ...pl, analistas: borrar ? pl.analistas : pl.analistas.filter((a) => !aqui.has(normalizarNombre(a))) }));
              if (!borrar) {
                const i = guardadas.findIndex((pl) => pl.id === id);
                if (i >= 0) lista[i] = { ...regla, id }; else lista = [...lista, { ...regla, id }];
              }
              siguientes[GENERAL] = { ...general, plantillas: lista.filter(Boolean) };
            } else if (nombre === GENERAL) {
              // Vaciar o cambiar la tabla general no toca las reglas guardadas.
              siguientes[GENERAL] = { ...(borrar ? { base: 0, niveles: [] } : regla), plantillas: guardadas };
            } else if (borrar) delete siguientes[nombre]; else siguientes[nombre] = regla;
            if (await guardarReglas(siguientes)) setEditando(null);
          }} />
      )}
    </div>
  );
}

function FilaDeAnalista({ f, es, td, abierto, onToggle, onEditar, pagos, nombreDeCliente }) {
  return (
    <>
      <tr>
        <td style={{ ...td, color: 'var(--c-white,#eef6ff)', fontWeight: 700 }}>
          <button type="button" onClick={onToggle} disabled={!f.ventas}
            style={{ all: 'unset', cursor: f.ventas ? 'pointer' : 'default', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {f.ventas ? (abierto ? <ChevronDown size={13} /> : <ChevronRight size={13} />) : null}{f.analista}
          </button>
        </td>
        <td style={td}>{f.ventas}</td>
        <td style={{ ...td, whiteSpace: 'normal' }}>
          {f.porTipo.length === 0
            ? (f.conReglas ? <span>{es ? 'Sin ventas' : 'No sales'}</span> : <span style={{ color: 'var(--c-amber,#ffb700)' }}>{es ? 'Sin reglas' : 'No rules'}</span>)
            : f.porTipo.map((t) => (
              <span key={t.tipo} style={{ display: 'inline-block', marginRight: 10 }}>
                {nombreDeTipo(t.tipo, es)}: {t.ventas} × {t.monto != null ? fmtMoney(t.monto) : '…'}
                {t.nivel ? <span style={{ color: 'var(--c-cyan,#00d4ff)' }}>{es ? ` (nivel ${t.nivel.desde}+)` : ` (level ${t.nivel.desde}+)`}</span> : null}
              </span>
            ))}
        </td>
        <td style={{ ...td, color: 'var(--c-green,#00ff88)', fontWeight: 700 }}>{fmtMoney(f.total)}</td>
        {onEditar && (
          <td style={td}>
            <button type="button" className="dkt-btn-g" onClick={onEditar}>{es ? 'Reglas' : 'Rules'}</button>
          </td>
        )}
      </tr>
      {abierto && f.polizas.map((p) => {
        const pago = pagos[p.id];
        const pt = getPolicyType(claveDeTipo(p.type));
        return (
          <tr key={p.id} style={{ background: 'rgba(0,212,255,.03)' }}>
            <td style={{ ...td, paddingLeft: '2rem' }}>{nombreDeCliente(p)}</td>
            <td style={td}>{p.saleDate ? fmt(p.saleDate) : '—'}</td>
            <td style={td}>{`${es ? (pt.labelEs || pt.label) : pt.label} · ${p.carrier || '—'}`}</td>
            <td style={td}>{fmtMoney(pago ? pago.monto : 0)}</td>
            {onEditar && <td style={td} />}
          </tr>
        );
      })}
    </>
  );
}

// Base + niveles de una regla (para cualquier tipo, o para un tipo).
function EditorDeParte({ es, valor, onChange, ejemplo }) {
  const inp = { width: '100%', boxSizing: 'border-box' };
  const niveles = valor.niveles || [];
  const ponerNivel = (i, cambio) => onChange({ ...valor, niveles: niveles.map((x, j) => (j === i ? { ...x, ...cambio } : x)) });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
      <label className="dkt-input-lbl">{es ? 'Base: $ fijo por póliza vendida' : 'Base: fixed $ per policy sold'}
        <input className="dkt-inp" inputMode="decimal" value={valor.base} onChange={(e) => onChange({ ...valor, base: e.target.value })} placeholder={ejemplo || '$25'} style={inp} />
      </label>
      <div>
        <div className="dkt-input-lbl">{es ? 'Niveles por número de pólizas en el mes' : 'Levels by number of policies in the month'}</div>
        {niveles.map((n, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '.78rem', color: 'var(--c-text2,#7aa3c0)' }}>{es ? 'Desde' : 'From'}</span>
            <input className="dkt-inp" inputMode="numeric" value={n.desde} onChange={(e) => ponerNivel(i, { desde: e.target.value })} placeholder="10" style={{ width: 70 }} />
            <span style={{ fontSize: '.78rem', color: 'var(--c-text2,#7aa3c0)' }}>{es ? 'pólizas →' : 'policies →'}</span>
            <input className="dkt-inp" inputMode="decimal" value={n.monto} onChange={(e) => ponerNivel(i, { monto: e.target.value })} placeholder="$35" style={{ width: 90 }} />
            <span style={{ fontSize: '.78rem', color: 'var(--c-text2,#7aa3c0)' }}>{es ? 'por póliza' : 'per policy'}</span>
            <button type="button" className="dkt-btn-g" onClick={() => onChange({ ...valor, niveles: niveles.filter((_, j) => j !== i) })} title={es ? 'Quitar nivel' : 'Remove level'}><Trash2 size={13} /></button>
          </div>
        ))}
        <button type="button" className="dkt-btn-g" onClick={() => onChange({ ...valor, niveles: [...niveles, { desde: '', monto: '' }] })}>
          <Plus size={13} /> {es ? 'Agregar nivel' : 'Add level'}
        </button>
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.78rem', color: 'var(--c-text2,#7aa3c0)', cursor: 'pointer' }}>
        <input type="checkbox" checked={!!valor.porMiembro} onChange={(e) => onChange({ ...valor, porMiembro: e.target.checked })} />
        {es ? 'Pagar por miembro (se multiplica por las personas aseguradas de la póliza; 1 si no tiene)' : 'Pay per member (multiplied by the policy’s insured people; 1 if none)'}
      </label>
    </div>
  );
}

const OPCIONES_DE_AJUSTE = (es) => [
  { id: 'sumar', label: es ? 'Sumar $' : 'Add $' },
  { id: 'sumarPct', label: es ? 'Sumar %' : 'Add %' },
  { id: 'multiplicar', label: es ? 'Multiplicar ×' : 'Multiply ×' },
  { id: 'restar', label: es ? 'Restar $' : 'Subtract $' },
  { id: 'restarPct', label: es ? 'Restar %' : 'Subtract %' },
  { id: 'pctPrima', label: es ? 'Pagar % de la prima' : 'Pay % of premium' },
  { id: 'sumarPctPrima', label: es ? 'Sumar % de la prima' : 'Add % of premium' },
];
const esDePrima = (op) => op === 'pctPrima' || op === 'sumarPctPrima';

// Tipo, compañía y fuente del lead de una línea o un ajuste ('' = cualquiera).
function Condiciones({ es, valor, onChange, companias, idLista }) {
  const sel = { minWidth: 0, flex: '1 1 140px' };
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      <select className="dkt-inp" value={valor.tipo || ''} onChange={(e) => onChange({ ...valor, tipo: e.target.value })} style={sel} title={es ? 'Tipo de póliza' : 'Policy type'}>
        <option value="">{es ? 'Cualquier tipo' : 'Any type'}</option>
        {POLICY_TYPES.map((p) => <option key={p.id} value={p.id}>{nombreDeTipo(p.id, es)}</option>)}
      </select>
      <input className="dkt-inp" list={idLista} value={valor.compania || ''} onChange={(e) => onChange({ ...valor, compania: e.target.value })}
        placeholder={es ? 'Cualquier compañía' : 'Any carrier'} style={sel} title={es ? 'Compañía (vacío = todas)' : 'Carrier (empty = all)'} />
      <datalist id={idLista}>{(companias || []).map((c) => <option key={c} value={c} />)}</datalist>
      <select className="dkt-inp" value={valor.fuente || ''} onChange={(e) => onChange({ ...valor, fuente: e.target.value })} style={sel} title={es ? 'Fuente del lead' : 'Lead source'}>
        <option value="">{es ? 'Cualquier fuente de lead' : 'Any lead source'}</option>
        {allLeadSources().map((f) => <option key={f.id} value={f.id}>{leadTypeLabel(f.id, es ? 'es' : 'en')}</option>)}
        {valor.fuente && !allLeadSources().some((f) => f.id === valor.fuente) && <option value={valor.fuente}>{leadTypeLabel(valor.fuente, es ? 'es' : 'en')}</option>}
      </select>
    </div>
  );
}

const aTexto = (r) => ({
  base: r && r.base ? String(r.base) : '',
  niveles: r ? (r.niveles || []).map((x) => ({ desde: String(x.desde), monto: String(x.monto) })) : [],
  porMiembro: !!(r && r.porMiembro),
});
const condicionesDe = (x) => ({ tipo: x?.tipo || '', compania: x?.compania || '', fuente: x?.fuente || '' });

function EditorDeReglas({ es, inicial, analistas, companias = [], usuarios = [], reglas, onClose, onGuardar }) {
  const [quien, setQuien] = useState(inicial || GENERAL);
  const [nuevo, setNuevo] = useState('');
  const guardadas = reglas[GENERAL]?.plantillas || [];
  const deGuardada = (q) => (q.startsWith('plantilla:') ? guardadas.find((pl) => pl.id === q.slice('plantilla:'.length)) : null);
  const cargar = (q) => {
    const r = q === '__nuevaPlantilla__' ? null : (deGuardada(q) || reglas[q]);
    return {
      todos: aTexto(r),
      porTipo: Object.fromEntries(Object.entries(r?.porTipo || {}).map(([t, rt]) => [t, aTexto(rt)])),
      lineas: (r?.lineas || []).map((l) => ({ ...condicionesDe(l), ...aTexto(l) })),
      ajustes: (r?.ajustes || []).map((a) => ({ ...condicionesDe(a), operacion: a.operacion, valor: String(a.valor), prima: a.prima || 'mensual' })),
      usuarioId: r?.usuarioId || '',
      nombre: r?.nombre || '',
      analistas: r?.analistas || [],
    };
  };
  const [form, setForm] = useState(() => cargar(inicial || GENERAL));
  const [tipoNuevo, setTipoNuevo] = useState('');
  // Una regla nueva se queda con su id aunque se cambie el nombre.
  const [idNuevo] = useState(() => `r${Date.now().toString(36)}`);
  const esGeneral = quien === GENERAL;
  const esGuardada = quien === '__nuevaPlantilla__' || quien.startsWith('plantilla:');
  const esAnalista = !esGeneral && !esGuardada;
  const clave = esGeneral ? GENERAL
    : esGuardada ? `plantilla:${quien === '__nuevaPlantilla__' ? idNuevo : quien.slice('plantilla:'.length)}`
      : claveDeAnalista(quien === '__nuevo__' ? nuevo : quien);
  const existe = esGuardada ? !!deGuardada(clave) : !!reglas[clave];
  const elegir = (q) => { setQuien(q); setForm(cargar(q === '__nuevo__' ? '' : q)); };
  const tiposPuestos = Object.keys(form.porTipo);
  const tiposLibres = POLICY_TYPES.filter((p) => !tiposPuestos.includes(p.id));
  const guardar = () => onGuardar(clave, {
    ...form.todos, porTipo: form.porTipo, lineas: form.lineas, ajustes: form.ajustes,
    ...(esAnalista ? { usuarioId: form.usuarioId } : {}),
    ...(esGuardada ? { nombre: form.nombre.trim(), analistas: form.analistas } : {}),
  }, false);
  const conGuardada = (a) => plantillaDe(reglas, a);
  const marcarAnalista = (a, si) => setForm((f) => ({ ...f, analistas: si ? [...f.analistas, a] : f.analistas.filter((x) => normalizarNombre(x) !== normalizarNombre(a)) }));
  const ponerLinea = (i, v) => setForm((f) => ({ ...f, lineas: f.lineas.map((x, j) => (j === i ? v : x)) }));
  const ponerAjuste = (i, v) => setForm((f) => ({ ...f, ajustes: f.ajustes.map((x, j) => (j === i ? v : x)) }));

  return (
    <Modal title={es ? 'Reglas de comisión de los analistas' : 'Analyst commission rules'} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '.9rem' }}>
        <label className="dkt-input-lbl">{es ? '¿Para quién?' : 'For whom?'}
          <select className="dkt-inp" value={quien} onChange={(e) => elegir(e.target.value)} style={{ width: '100%' }}>
            <option value={GENERAL}>{es ? '⭐ Tabla general (todos los analistas)' : '⭐ General table (all analysts)'}</option>
            <optgroup label={es ? 'Reglas guardadas' : 'Saved rules'}>
              {guardadas.map((pl) => <option key={pl.id} value={`plantilla:${pl.id}`}>📋 {pl.nombre} ({pl.analistas.length} {es ? 'analista(s)' : 'analyst(s)'})</option>)}
              <option value="__nuevaPlantilla__">{es ? '+ Nueva regla guardada…' : '+ New saved rule…'}</option>
            </optgroup>
            <optgroup label={es ? 'Un analista (individual)' : 'One analyst (individual)'}>
              {analistas.map((a) => (
                <option key={a} value={a}>{a}{reglas[a] ? (es ? ' · con reglas propias' : ' · own rules') : ''}{conGuardada(a) ? ` · 📋 ${conGuardada(a).nombre}` : ''}</option>
              ))}
              <option value="__nuevo__">{es ? '+ Otro analista…' : '+ Another analyst…'}</option>
            </optgroup>
          </select>
        </label>
        {esGuardada && (
          <>
            <label className="dkt-input-lbl">{es ? 'Nombre de la regla' : 'Rule name'}
              <input className="dkt-inp" value={form.nombre} onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
                placeholder={es ? 'Ej.: Regla 1 — Analistas nuevos' : 'E.g.: Rule 1 — New analysts'} style={{ width: '100%' }} />
            </label>
            <div className="dkt-card" style={{ padding: '.8rem' }}>
              <div style={{ fontSize: '.78rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)', marginBottom: '.3rem' }}>
                {es ? 'Analistas que usan esta regla' : 'Analysts using this rule'}
              </div>
              <div style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)', marginBottom: '.5rem' }}>
                {es ? 'Cada analista usa UNA regla guardada: si ya estaba en otra, se pasa a esta. Lo individual de cada analista sigue ganando sobre la regla guardada.'
                  : 'Each analyst uses ONE saved rule: if they were in another, they move to this one. Each analyst’s individual rules still win over the saved rule.'}
              </div>
              {analistas.length === 0 && <div style={{ fontSize: '.75rem', color: 'var(--c-text3,#45688a)' }}>{es ? 'Todavía no hay analistas en las pólizas.' : 'No analysts on policies yet.'}</div>}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.4rem 1rem' }}>
                {analistas.map((a) => {
                  const dentro = form.analistas.some((x) => normalizarNombre(x) === normalizarNombre(a));
                  const otra = conGuardada(a);
                  return (
                    <label key={a} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.8rem', color: 'var(--c-text,#c8dff0)', cursor: 'pointer' }}>
                      <input type="checkbox" checked={dentro} onChange={(e) => marcarAnalista(a, e.target.checked)} />
                      {a}{otra && clave !== `plantilla:${otra.id}` ? <span style={{ color: 'var(--c-text3,#45688a)' }}> ({es ? 'ahora en' : 'now in'} {otra.nombre})</span> : null}
                    </label>
                  );
                })}
              </div>
            </div>
          </>
        )}
        {quien === '__nuevo__' && (
          <label className="dkt-input-lbl">{es ? 'Nombre del analista' : 'Analyst name'}
            <input className="dkt-inp" value={nuevo} onChange={(e) => setNuevo(e.target.value)} placeholder={es ? 'Tal como sale en la póliza' : 'As it appears on the policy'} style={{ width: '100%' }} />
          </label>
        )}
        <div style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)' }}>
          {esGeneral
            ? (es ? 'La tabla general vale para TODOS los analistas. Si a alguien le pagas distinto, ponlo en una regla guardada o elígelo abajo y ponle sus propios montos: ganan sobre la general.'
              : 'The general table applies to ALL analysts. If you pay someone differently, put them in a saved rule or pick them below and set their own amounts: those win over the general.')
            : esGuardada
              ? (es ? 'Una regla guardada se arma una vez y se le asignan los analistas. Gana sobre la tabla general; lo individual de cada analista gana sobre ella.'
                : 'A saved rule is set up once and assigned to analysts. It wins over the general table; each analyst’s individual rules win over it.')
              : (es ? 'Lo que pongas aquí gana sobre su regla guardada y sobre la tabla general. Lo que dejes vacío se paga con esas.'
                : 'What you set here wins over their saved rule and the general table. Anything left empty is paid with those.')}
        </div>

        {esAnalista && (
          <label className="dkt-input-lbl">{es ? 'Su usuario de Deskte (para que vea solo lo suyo)' : 'Their Deskte user (so they see only their own)'}
            <select className="dkt-inp" value={form.usuarioId} onChange={(e) => setForm((f) => ({ ...f, usuarioId: e.target.value }))} style={{ width: '100%' }}>
              <option value="">{es ? '— Por el nombre (si es igual que su usuario) —' : '— By name (if it matches their user) —'}</option>
              {(usuarios || []).map((u) => <option key={u.id} value={u.id}>{u.name}{u.email ? ` · ${u.email}` : ''}</option>)}
            </select>
          </label>
        )}

        <div className="dkt-card" style={{ padding: '.8rem' }}>
          <div style={{ fontSize: '.78rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)', marginBottom: '.5rem' }}>
            {es ? 'Por tipo de póliza' : 'By policy type'}
          </div>
          {tiposPuestos.length === 0 && (
            <div style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)', marginBottom: '.5rem' }}>
              {es ? 'Todavía no hay montos por tipo. Agrega un tipo para pagar distinto Salud, Vida, Medicare…' : 'No amounts by type yet. Add a type to pay Health, Life, Medicare… differently.'}
            </div>
          )}
          {tiposPuestos.map((t) => (
            <div key={t} style={{ borderTop: '1px solid var(--c-border,#1a3550)', paddingTop: '.6rem', marginTop: '.6rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.4rem' }}>
                <strong style={{ fontSize: '.8rem', color: 'var(--c-white,#eef6ff)' }}>{nombreDeTipo(t, es)}</strong>
                <button type="button" className="dkt-btn-g" onClick={() => setForm((f) => { const { [t]: _fuera, ...resto } = f.porTipo; return { ...f, porTipo: resto }; })}>
                  <Trash2 size={13} /> {es ? 'Quitar tipo' : 'Remove type'}
                </button>
              </div>
              <EditorDeParte es={es} valor={form.porTipo[t]} onChange={(v) => setForm((f) => ({ ...f, porTipo: { ...f.porTipo, [t]: v } }))} />
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, marginTop: '.7rem', flexWrap: 'wrap' }}>
            <select className="dkt-inp" value={tipoNuevo} onChange={(e) => setTipoNuevo(e.target.value)} style={{ minWidth: 200 }}>
              <option value="">{es ? 'Elige un tipo de póliza…' : 'Pick a policy type…'}</option>
              {tiposLibres.map((p) => <option key={p.id} value={p.id}>{nombreDeTipo(p.id, es)}</option>)}
            </select>
            <button type="button" className="dkt-btn-s" disabled={!tipoNuevo}
              onClick={() => { setForm((f) => ({ ...f, porTipo: { ...f.porTipo, [tipoNuevo]: { base: '', niveles: [] } } })); setTipoNuevo(''); }}>
              <Plus size={13} /> {es ? 'Agregar tipo' : 'Add type'}
            </button>
          </div>
        </div>

        <div className="dkt-card" style={{ padding: '.8rem' }}>
          <div style={{ fontSize: '.78rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)', marginBottom: '.3rem' }}>
            {es ? 'Precios especiales por compañía o fuente de lead' : 'Special prices by carrier or lead source'}
          </div>
          <div style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)', marginBottom: '.5rem' }}>
            {es ? 'Ej.: Salud + Ambetter $40, o Salud + Facebook $15. Lo que dejes en «Cualquier…» vale para todo. Ganan sobre el precio del tipo; si una póliza cumple varias, gana la más detallada.'
              : 'E.g.: Health + Ambetter $40, or Health + Facebook $15. Anything left as “Any…” applies to all. They win over the type price; if a policy matches several, the most detailed one wins.'}
          </div>
          {form.lineas.map((l, i) => (
            <div key={i} style={{ borderTop: '1px solid var(--c-border,#1a3550)', paddingTop: '.6rem', marginTop: '.6rem', display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Condiciones es={es} valor={l} companias={companias} idLista={`dkt-companias-linea-${i}`} onChange={(v) => ponerLinea(i, { ...l, ...v })} />
                </div>
                <button type="button" className="dkt-btn-g" title={es ? 'Quitar' : 'Remove'} onClick={() => setForm((f) => ({ ...f, lineas: f.lineas.filter((_, j) => j !== i) }))}><Trash2 size={13} /></button>
              </div>
              <EditorDeParte es={es} valor={l} onChange={(v) => ponerLinea(i, { ...l, ...v })} />
            </div>
          ))}
          <button type="button" className="dkt-btn-s" style={{ marginTop: '.7rem' }}
            onClick={() => setForm((f) => ({ ...f, lineas: [...f.lineas, { tipo: '', compania: '', fuente: '', base: '', niveles: [], porMiembro: false }] }))}>
            <Plus size={13} /> {es ? 'Agregar precio especial' : 'Add special price'}
          </button>
        </div>

        <div className="dkt-card" style={{ padding: '.8rem' }}>
          <div style={{ fontSize: '.78rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)', marginBottom: '.5rem' }}>
            {es ? 'Cualquier otro tipo de póliza' : 'Any other policy type'}
          </div>
          <EditorDeParte es={es} valor={form.todos} onChange={(v) => setForm((f) => ({ ...f, todos: v }))} />
        </div>

        <div className="dkt-card" style={{ padding: '.8rem' }}>
          <div style={{ fontSize: '.78rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)', marginBottom: '.3rem' }}>
            {es ? 'Ajustes propios' : 'Custom adjustments'}
          </div>
          <div style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)', marginBottom: '.5rem' }}>
            {es ? 'Cambian lo que se paga por póliza cuando se cumplen sus condiciones: sumar o restar un monto o un %, multiplicar, o sacar un % de la prima (mensual o anualizada). «Pagar % de la prima» hace que la comisión SEA ese %, aunque no haya precio puesto; «Sumar % de la prima» lo suma. Ej.: «Fuente Facebook → Sumar 10%» o «Salud → Pagar 5% de la prima anualizada». Se aplican todos los que coincidan, en este orden (tabla general, regla guardada y luego el analista), y nunca bajan de $0.'
              : 'They change what each policy pays when their conditions match: add or subtract an amount or a %, multiply, or take a % of the premium (monthly or annualized). “Pay % of premium” makes the commission BE that %, even with no price set; “Add % of premium” adds it. E.g.: “Lead source Facebook → Add 10%” or “Health → Pay 5% of annualized premium”. Every matching one applies, in this order (general table, saved rule, then the analyst), and never below $0.'}
          </div>
          {form.ajustes.map((a, i) => (
            <div key={i} style={{ borderTop: '1px solid var(--c-border,#1a3550)', paddingTop: '.6rem', marginTop: '.6rem', display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
              <Condiciones es={es} valor={a} companias={companias} idLista={`dkt-companias-ajuste-${i}`} onChange={(v) => ponerAjuste(i, { ...a, ...v })} />
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <select className="dkt-inp" value={a.operacion} onChange={(e) => ponerAjuste(i, { ...a, operacion: e.target.value })} style={{ minWidth: 150 }}>
                  {OPCIONES_DE_AJUSTE(es).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
                {esDePrima(a.operacion) && (
                  <select className="dkt-inp" value={a.prima || 'mensual'} onChange={(e) => ponerAjuste(i, { ...a, prima: e.target.value })} style={{ minWidth: 140 }}>
                    <option value="mensual">{es ? 'Prima mensual' : 'Monthly premium'}</option>
                    <option value="anual">{es ? 'Prima anualizada' : 'Annualized premium'}</option>
                  </select>
                )}
                <input className="dkt-inp" inputMode="decimal" value={a.valor} onChange={(e) => ponerAjuste(i, { ...a, valor: e.target.value })}
                  placeholder={a.operacion === 'multiplicar' ? '1.5' : (a.operacion.endsWith('Pct') || esDePrima(a.operacion) ? '10%' : '$5')} style={{ width: 100 }} />
                <button type="button" className="dkt-btn-g" title={es ? 'Quitar' : 'Remove'} onClick={() => setForm((f) => ({ ...f, ajustes: f.ajustes.filter((_, j) => j !== i) }))}><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
          <button type="button" className="dkt-btn-s" style={{ marginTop: '.7rem' }}
            onClick={() => setForm((f) => ({ ...f, ajustes: [...f.ajustes, { tipo: '', compania: '', fuente: '', operacion: 'sumarPct', valor: '', prima: 'mensual' }] }))}>
            <Plus size={13} /> {es ? 'Agregar ajuste' : 'Add adjustment'}
          </button>
        </div>

        <div style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)' }}>
          {es
            ? 'Ejemplo: Salud base $25 y "desde 10 pólizas → $35". Si en el mes vende 12 de Salud, las 12 se pagan a $35 = $420. Las de Vida cuentan aparte, con su propio monto. Con «Pagar por miembro», una póliza de 4 personas paga 4 × $35; los niveles se cuentan por pólizas. Orden: lo del analista (precio especial → por tipo → cualquier otro tipo) → lo mismo en la tabla general. Después, los ajustes.'
            : 'Example: Health base $25 and "from 10 policies → $35". If they sell 12 Health in the month, all 12 pay $35 = $420. Life counts separately, with its own amount. With “Pay per member”, a 4-person policy pays 4 × $35; levels count policies. Order: the analyst’s (special price → by type → any other type) → the same in the general table. Then the adjustments.'}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: '.3rem' }}>
          {existe
            ? <button type="button" className="dkt-btn-d" onClick={() => onGuardar(clave, null, true)}>
              {esGeneral ? (es ? 'Vaciar tabla general' : 'Clear general table')
                : esGuardada ? (es ? 'Borrar esta regla guardada' : 'Delete this saved rule')
                  : (es ? 'Quitar sus reglas' : 'Remove their rules')}
            </button>
            : <span />}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="dkt-btn-s" onClick={onClose}>{es ? 'Cancelar' : 'Cancel'}</button>
            <button type="button" className="dkt-btn-p" disabled={!clave || (esGuardada && !form.nombre.trim())} onClick={guardar}>{es ? 'Guardar' : 'Save'}</button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
